/**
 * Debounce Utility
 * 
 * Creates a debounced function that delays invoking `func` until after
 * `wait` milliseconds have elapsed since the last time the debounced
 * function was invoked.
 * 
 * This is particularly useful for:
 * - Preventing excessive API calls during rapid user input
 * - Optimizing performance for expensive operations
 * - Reducing server load from real-time updates
 * 
 * @param func - The function to debounce
 * @param wait - The number of milliseconds to delay
 * @returns A debounced version of the function
 * 
 * @example
 * ```typescript
 * const debouncedSearch = debounce((query: string) => {
 *   searchAPI(query);
 * }, 500);
 * 
 * // User types "hello"
 * debouncedSearch("h");    // Starts 500ms timer
 * debouncedSearch("he");   // Cancels previous, starts new 500ms timer
 * debouncedSearch("hel");  // Cancels previous, starts new 500ms timer
 * debouncedSearch("hell"); // Cancels previous, starts new 500ms timer
 * debouncedSearch("hello"); // Cancels previous, starts new 500ms timer
 * // After 500ms of no calls, searchAPI("hello") is finally invoked
 * ```
 */
export type DebouncedFunction<T extends (...args: any[]) => any> = ((...args: Parameters<T>) => void) & {
  /** Drops the pending call, if any. */
  cancel: () => void;
  /** Runs the pending call immediately, if any. Use before unmount/save so edits are not lost. */
  flush: () => void;
  /** Whether a call is waiting to run. */
  isPending: () => boolean;
};

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): DebouncedFunction<T> {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let pendingArgs: Parameters<T> | null = null;

  const run = () => {
    const args = pendingArgs;
    timeout = null;
    pendingArgs = null;
    if (args) {
      func(...args);
    }
  };

  const debouncedFunc = (...args: Parameters<T>) => {
    // Clear any existing timeout
    if (timeout) {
      clearTimeout(timeout);
    }

    // Set a new timeout
    pendingArgs = args;
    timeout = setTimeout(run, wait);
  };

  // Add cancel method to clear pending timeout
  debouncedFunc.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = null;
    pendingArgs = null;
  };

  debouncedFunc.flush = () => {
    if (timeout) {
      clearTimeout(timeout);
      run();
    }
  };

  debouncedFunc.isPending = () => timeout !== null;

  return debouncedFunc as DebouncedFunction<T>;
}

/**
 * Debounce with immediate execution option
 * 
 * Creates a debounced function that can optionally execute immediately
 * on the leading edge of the timeout instead of the trailing edge.
 * 
 * @param func - The function to debounce
 * @param wait - The number of milliseconds to delay
 * @param immediate - Whether to execute on the leading edge
 * @returns A debounced version of the function
 * 
 * @example
 * ```typescript
 * const debouncedSave = debounce((data: any) => {
 *   saveToAPI(data);
 * }, 500, true);
 * 
 * // First call executes immediately
 * debouncedSave(data1); // Executes saveToAPI(data1) immediately
 * debouncedSave(data2); // Ignored (within 500ms)
 * debouncedSave(data3); // Ignored (within 500ms)
 * // After 500ms of no calls, can be called immediately again
 * ```
 */
export function debounceImmediate<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  immediate = false
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    const callNow = immediate && !timeout;

    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => {
      timeout = null;
      if (!immediate) {
        func(...args);
      }
    }, wait);

    if (callNow) {
      func(...args);
    }
  };
}

/**
 * Throttle Utility
 * 
 * Creates a throttled function that only invokes `func` at most once per
 * every `wait` milliseconds. Unlike debounce, throttle guarantees the
 * function is called at regular intervals during continuous invocations.
 * 
 * This is useful for:
 * - Rate-limiting event handlers (scroll, resize, mousemove)
 * - Ensuring periodic updates during continuous activity
 * - Performance optimization for high-frequency events
 * 
 * @param func - The function to throttle
 * @param wait - The number of milliseconds to throttle invocations to
 * @returns A throttled version of the function
 * 
 * @example
 * ```typescript
 * const throttledScroll = throttle(() => {
 *   updateScrollPosition();
 * }, 100);
 * 
 * window.addEventListener('scroll', throttledScroll);
 * // updateScrollPosition will be called at most once every 100ms
 * ```
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let lastCall = 0;
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    const now = Date.now();
    const timeSinceLastCall = now - lastCall;

    if (timeSinceLastCall >= wait) {
      lastCall = now;
      func(...args);
    } else {
      // Schedule a call at the end of the wait period
      if (timeout) {
        clearTimeout(timeout);
      }
      timeout = setTimeout(() => {
        lastCall = Date.now();
        func(...args);
      }, wait - timeSinceLastCall);
    }
  };
}
